import type {
  Feature,
  FeatureType,
  Params,
  ParamValues,
  FeatureParams,
  FeatureRequiredParams,
} from './feature';
import { FEATURE_PARAMS } from './feature';

// `UnionFromTuple<["a", "b", "c"]>` === `"a" | "b" | "c"`
type UnionFromTuple<T extends readonly string[]> = T[number];

// similar to `ParamType` from `src/feature.ts`, but with names instead of position
// - `NamedParamType<"addPanel", "componentType"> === string`
// - `NamedParamType<"viewport", "size"> === "phone" | "tablet" | "desktop"`
// - `NamedParamType<"viewport", "someUnknownParam"> === never`
type NamedParamType<
  T extends FeatureType,
  K extends UnionFromTuple<FeatureParams[T]>,
> = (T extends keyof ParamValues
  ? K extends keyof ParamValues[T]
    ? ParamValues[T][K]
    : string[]
  : string[])[number]; // I have no idea why `ParamValues[T][K][number]` doesn't work, so i worked around it

type FeatureWithRequiredParams = keyof FeatureRequiredParams;

export type NamedParams<T extends FeatureType> =
  (T extends FeatureWithRequiredParams
    ? {
        [K in UnionFromTuple<FeatureRequiredParams[T]>]: NamedParamType<T, K>;
      }
    : unknown) & {
    [K in UnionFromTuple<FeatureParams[T]>]?: NamedParamType<T, K>;
  };

// using `T extends FeatureType ? U : never` get union of generics instead of generic of union:
// e.g. `G<"addPanel"> | G<"appMarket"> | …` instead of `G<"addPanel" | "appMarket" | …>`
export type FeatureWithNamed<T extends FeatureType = FeatureType> =
  T extends FeatureType
    ? Omit<Feature<T>, 'params'> & {
        named: NamedParams<T>;
      }
    : never;

export function convertParamsToNamed<T extends FeatureType>(
  feature: Feature<T>,
): NamedParams<T>;
export function convertParamsToNamed<T extends FeatureType>(
  type: T,
  params: Params<T>,
): NamedParams<T>;
export function convertParamsToNamed<T extends FeatureType>(
  ...args: [Feature<T>] | [T, Params<T>]
): NamedParams<T> {
  let type: T;
  let params: Params<T>;

  if (args.length === 1) {
    type = args[0].type;
    params = args[0].params;
  } else {
    type = args[0];
    params = args[1];
  }

  const names: readonly string[] = FEATURE_PARAMS[type] || [];

  if (!params) {
    return {} as NamedParams<T>;
  }

  return (params as string[]).reduce((acc, param, i) => {
    const name = names[i];

    if (name) {
      acc[name] = param?.toString() || '';
    }

    return acc;
  }, {} as Record<string, string>) as NamedParams<T>;
}

export function convertNamedToParams<T extends FeatureType>(
  type: T,
  named: NamedParams<T>,
): Params<T> {
  const names: readonly string[] = FEATURE_PARAMS[type] || [];
  const namedRecord: Record<string, string | undefined> = named || {};

  const includedNames = names.map((name) =>
    // `v == null` is true for both `v = null` and `v = undefined`
    namedRecord[name] == null ? undefined : name,
  );

  while (
    includedNames.length &&
    includedNames[includedNames.length - 1] === undefined
  ) {
    includedNames.pop();
  }

  return includedNames.reduce((acc, name) => {
    if (!name) {
      acc.push('');
    } else {
      acc.push(namedRecord[name]?.toString() || '');
    }
    return acc;
  }, [] as string[]) as Params<T>;
}
